import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { FormField, FormOption, ImageOption } from '../../../types/formBuilder';

interface TypeSpecificSettingsProps {
  field: FormField;
  onUpdate: (updates: Partial<FormField>) => void;
}

export const TypeSpecificSettings: React.FC<TypeSpecificSettingsProps> = ({ field, onUpdate }) => {
  // Handle select options
  const addOption = () => {
    const newOptions = [...(field.options || []), { label: '', value: '' }];
    onUpdate({ options: newOptions });
  };

  const updateOption = (index: number, updates: Partial<FormOption>) => {
    const newOptions = [...(field.options || [])];
    newOptions[index] = { ...newOptions[index], ...updates };
    onUpdate({ options: newOptions });
  };

  const removeOption = (index: number) => {
    const newOptions = [...(field.options || [])];
    newOptions.splice(index, 1);
    onUpdate({ options: newOptions });
  };

  // Handle image options
  const addImageOption = () => {
    const newImageOptions = [...(field.imageOptions || []), { label: '', value: '', imageUrl: '' }];
    onUpdate({ imageOptions: newImageOptions });
  };

  const updateImageOption = (index: number, updates: Partial<ImageOption>) => {
    const newImageOptions = [...(field.imageOptions || [])];
    newImageOptions[index] = { ...newImageOptions[index], ...updates };
    onUpdate({ imageOptions: newImageOptions });
  };

  const removeImageOption = (index: number) => {
    const newImageOptions = [...(field.imageOptions || [])];
    newImageOptions.splice(index, 1);
    onUpdate({ imageOptions: newImageOptions });
  };

  switch (field.type) {
    case 'textarea':
      return (
        <div className="space-y-4">
          {/* Rows Setting */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-th-primary">
              Number of Rows
            </label>
            <input
              type="number"
              min="2"
              max="20"
              value={field.rows || 4}
              onChange={(e) => onUpdate({ rows: parseInt(e.target.value) || 4 })}
              className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary focus:border-brand focus:outline-none transition-all"
            />
            <p className="text-xs text-th-secondary">
              Height of the textarea in rows (default: 4)
            </p>
          </div>

          {/* Prompt Enhancement Settings */}
          <div className="space-y-4 p-4 bg-brand/5 border border-brand/20 rounded-lg">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={field.enablePromptEnhancement || false}
                onChange={(e) => onUpdate({ enablePromptEnhancement: e.target.checked })}
                className="w-4 h-4 text-brand bg-elevated border-subtle rounded focus:ring-brand focus:ring-2"
              />
              <div>
                <span className="text-th-primary font-medium">Enable Prompt Enhancement</span>
                <p className="text-th-secondary text-xs">Add AI-powered prompt enhancement button</p>
              </div>
            </div>

            {field.enablePromptEnhancement && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-th-primary">
                  System Instructions for Enhancement
                </label>
                <textarea
                  value={field.systemInstructions || ''}
                  onChange={(e) => onUpdate({ systemInstructions: e.target.value })}
                  placeholder="You are a professional prompt engineer. Enhance the user's prompt to be more detailed and specific for better AI generation results. Keep the core intent but add descriptive details, artistic style suggestions, and technical parameters that would improve the output quality."
                  rows={4}
                  className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none resize-none transition-all"
                />
                <p className="text-xs text-th-secondary">
                  Instructions for OpenAI on how to enhance user prompts
                </p>
              </div>
            )}
          </div>
        </div>
      );

    case 'number':
    case 'range':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">
                Minimum
              </label>
              <input
                type="number"
                value={field.min || ''}
                onChange={(e) => onUpdate({ min: parseFloat(e.target.value) || undefined })}
                placeholder="0"
                className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">
                Maximum
              </label>
              <input
                type="number"
                value={field.max || ''}
                onChange={(e) => onUpdate({ max: parseFloat(e.target.value) || undefined })}
                placeholder="100"
                className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">
                Step
              </label>
              <input
                type="number"
                value={field.step || ''}
                onChange={(e) => onUpdate({ step: parseFloat(e.target.value) || undefined })}
                placeholder="1"
                className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-th-primary">
              Default Value
            </label>
            <input
              type="number"
              value={field.defaultValue || ''}
              onChange={(e) => onUpdate({ defaultValue: parseFloat(e.target.value) || undefined })}
              placeholder="Default value"
              className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
            />
          </div>
        </div>
      );

    case 'select':
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-th-primary">
              Options
            </label>
            <button
              onClick={addOption}
              className="flex items-center space-x-1 px-3 py-1.5 bg-brand text-surface rounded-lg text-sm font-medium hover:bg-brand/90 transition-all"
            >
              <Plus className="w-3 h-3" />
              <span>Add Option</span>
            </button>
          </div>
          
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {(field.options || []).map((option, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-elevated border border-subtle rounded-lg">
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={option.label}
                    onChange={(e) => updateOption(index, { label: e.target.value })}
                    placeholder="Display Label"
                    className="bg-surface border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm"
                  />
                  <input
                    type="text"
                    value={option.value}
                    onChange={(e) => updateOption(index, { value: e.target.value })}
                    placeholder="api_value"
                    className="bg-surface border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm font-mono"
                  />
                </div>
                <button
                  onClick={() => removeOption(index)}
                  className="p-2 text-th-secondary hover:text-status-error transition-colors rounded hover:bg-status-error/10"
                  title="Remove option"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          
          {(!field.options || field.options.length === 0) && (
            <div className="text-center py-6 text-th-secondary">
              <p className="text-sm">No options added yet</p>
              <p className="text-xs">Click "Add Option" to create dropdown choices</p>
            </div>
          )}
        </div>
      );

    case 'file':
      return (
        <div className="space-y-4">
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={field.multiple || false}
                onChange={(e) => onUpdate({ multiple: e.target.checked })}
                className="w-4 h-4 text-brand bg-elevated border-subtle rounded focus:ring-brand focus:ring-2"
              />
              <div>
                <span className="text-th-primary font-medium">Allow Multiple Files</span>
                <p className="text-th-secondary text-xs">Users can select multiple files</p>
              </div>
            </label>

            {field.multiple && (
              <div className="space-y-2 ml-7">
                <label className="block text-sm font-medium text-th-primary">
                  File Limit
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={field.fileLimit || 5}
                  onChange={(e) => onUpdate({ fileLimit: parseInt(e.target.value) || 5 })}
                  className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary focus:border-brand focus:outline-none transition-all"
                />
                <p className="text-xs text-th-secondary">
                  Maximum number of files users can select (1-20)
                </p>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">
                Accepted File Types
              </label>
              <input
                type="text"
                value={field.accept || ''}
                onChange={(e) => onUpdate({ accept: e.target.value })}
                placeholder="image/*,video/*,.pdf"
                className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all font-mono"
              />
              <p className="text-xs text-th-secondary">
                File types (e.g., "image/*", "video/*", ".pdf", ".jpg,.png")
              </p>
            </div>
          </div>
        </div>
      );

    case 'dropdown_with_image':
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-th-primary">
              Image Options
            </label>
            <button
              onClick={addImageOption}
              className="flex items-center space-x-1 px-3 py-1.5 bg-accent text-surface rounded-lg text-sm font-medium hover:bg-accent/90 transition-all"
            >
              <Plus className="w-3 h-3" />
              <span>Add Option</span>
            </button>
          </div>
          
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {(field.imageOptions || []).map((option, index) => (
              <div key={index} className="p-4 bg-elevated border border-subtle rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-th-primary font-medium text-sm">Option {index + 1}</span>
                  <button
                    onClick={() => removeImageOption(index)}
                    className="p-1.5 text-th-secondary hover:text-status-error transition-colors rounded hover:bg-status-error/10"
                    title="Remove option"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={option.label}
                    onChange={(e) => updateImageOption(index, { label: e.target.value })}
                    placeholder="Display Label"
                    className="bg-surface border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm"
                  />
                  <input
                    type="text"
                    value={option.value}
                    onChange={(e) => updateImageOption(index, { value: e.target.value })}
                    placeholder="api_value"
                    className="bg-surface border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm font-mono"
                  />
                </div>
                
                <input
                  type="url"
                  value={option.imageUrl}
                  onChange={(e) => updateImageOption(index, { imageUrl: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="w-full bg-surface border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm"
                />
                
                {option.imageUrl && (
                  <div className="w-16 h-16 rounded overflow-hidden bg-surface">
                    <img
                      src={option.imageUrl}
                      alt={option.label}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = '<div class="w-full h-full flex items-center justify-center text-th-secondary bg-status-error/10"><span class="text-xs">Error</span></div>';
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Custom Value Settings */}
          <div className="space-y-3 p-4 bg-accent/5 border border-accent/20 rounded-lg">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={field.allowCustomValue || false}
                onChange={(e) => onUpdate({ allowCustomValue: e.target.checked })}
                className="w-4 h-4 text-accent bg-elevated border-subtle rounded focus:ring-accent focus:ring-2"
              />
              <div>
                <span className="text-th-primary font-medium">Allow Custom Values</span>
                <p className="text-th-secondary text-xs">Users can enter their own values</p>
              </div>
            </label>

            {field.allowCustomValue && (
              <div className="space-y-3 ml-7">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-th-primary">
                    Custom Value Label
                  </label>
                  <input
                    type="text"
                    value={field.customValueLabel || ''}
                    onChange={(e) => onUpdate({ customValueLabel: e.target.value })}
                    placeholder="Enter Custom Value"
                    className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-th-primary">
                    Custom Button Label
                  </label>
                  <input
                    type="text"
                    value={field.customButtonLabel || ''}
                    onChange={(e) => onUpdate({ customButtonLabel: e.target.value })}
                    placeholder="Use Custom Value"
                    className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-th-primary">
                    Custom Input Placeholder
                  </label>
                  <input
                    type="text"
                    value={field.customInputPlaceholder || ''}
                    onChange={(e) => onUpdate({ customInputPlaceholder: e.target.value })}
                    placeholder="Type your custom value..."
                    className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                  />
                </div>
              </div>
            )}
          </div>

          {(!field.imageOptions || field.imageOptions.length === 0) && (
            <div className="text-center py-6 text-th-secondary">
              <p className="text-sm">No image options added yet</p>
              <p className="text-xs">Click "Add Option" to create choices with images</p>
            </div>
          )}
        </div>
      );

    default:
      return null;
  }
};